/**
 * IncognitoWarning - Warns users in private browsing mode
 */

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getIncognitoStatus } from '@/utils/incognitoDetection';
import { cn } from '@/utils/cn';

export function IncognitoWarning() {
  const [isIncognito, setIsIncognito] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    getIncognitoStatus().then((status) => {
      setIsIncognito(status);
    });
  }, []);

  if (!isIncognito || isDismissed) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className={cn(
          'fixed top-0 left-0 right-0 z-50',
          'bg-gradient-to-r from-orange-500/20 to-red-500/20',
          'border-b border-orange-500/30 backdrop-blur-sm'
        )}
      >
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            {/* Warning icon */}
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-orange-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>

            {/* Warning text */}
            <div className="flex-1">
              <p className="text-sm font-medium text-orange-200">
                🔒 Incognito/Private Mode Detected
              </p>
              <p className="text-xs text-orange-300/80">
                Your scent reports and comments may be lost when you close this tab.
                For permanent storage, use normal browsing mode or{' '}
                <button className="underline hover:text-orange-200">log in</button>.
              </p>
            </div>
          </div>

          {/* Dismiss button */}
          <button
            onClick={() => setIsDismissed(true)}
            className="flex-shrink-0 p-1 rounded hover:bg-white/10 transition-colors"
            aria-label="Dismiss warning"
          >
            <svg
              className="w-5 h-5 text-orange-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

export default IncognitoWarning;
